import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import NavigationLink from 'modules/payroll_bundle/components/PolicyPeriodDashboardContent/RegularCycleSummary/ProcessPayrollStatusOverview/_NavigationLink'
import { getDashboardButtonCta } from 'modules/payroll_bundle/components/PolicyPeriodDashboardContent/RegularCycleSummary/copies'
import { PayrollStatus } from 'modules/payroll_bundle/lib/enums'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'

interface SummaryDetailsProps {
  status: PayrollStatus
  canEditSupplements: boolean
  canSeeCompanyPayrollResults: boolean
}

const ProcessPayrollStatusOverview = ({
  status,
  canEditSupplements,
  canSeeCompanyPayrollResults,
}: SummaryDetailsProps) => {
  const { navigation } = useNavigation()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const navigate = useNavigate()

  const { policyId, currentPeriod } = usePolicyPeriods()
  const periodId = currentPeriod?.id

  const isPayrollCalculationStatus = status === PayrollStatus.PAYROLL_CALCULATION
  const isPaymentStatus =
    status === PayrollStatus.PAID || status === PayrollStatus.PAYMENT_PROCESSING

  const paymentsCompleted = status === PayrollStatus.PAID
  const resultsCompleted = status === PayrollStatus.PAYMENT_PROCESSING || paymentsCompleted
  const supplementsCompleted = status === PayrollStatus.PAYROLL_CALCULATION || resultsCompleted

  const isSupplementStage =
    status === PayrollStatus.PREPARATION ||
    status === PayrollStatus.SUPPLEMENTS_DEFINITION ||
    status === PayrollStatus.UNDER_REVIEW

  const paymentsDisabled = !canSeeCompanyPayrollResults || isSupplementStage

  const newCycleWorflowCtaPath = () => {
    // These are guaranteed to be present according to the render hierarchy.
    const pathParams = {
      policyId: policyId!, // eslint-disable-line no-type-assertion/no-type-assertion
      periodId: periodId!, // eslint-disable-line no-type-assertion/no-type-assertion
    }

    if (canSeeCompanyPayrollResults && isPayrollCalculationStatus) {
      return navigation.payrollBundle.policy.period.cycles.results.fullPath(pathParams)
    } else if (canSeeCompanyPayrollResults && isPaymentStatus) {
      return navigation.payrollBundle.policy.period.cycles.payments.fullPath(pathParams)
    }
    return navigation.payrollBundle.policy.period.cycles.supplements.fullPath(pathParams)
  }

  const createNavigationMap = () => {
    const { supplements, results, payments } = navigation.payrollBundle.policy.period.cycles

    return {
      [PayrollStatus.PREPARATION]: supplements.fullPath,
      [PayrollStatus.SUPPLEMENTS_DEFINITION]: supplements.fullPath,
      [PayrollStatus.UNDER_REVIEW]: supplements.fullPath,
      [PayrollStatus.PAYROLL_CALCULATION]: results.fullPath,
      [PayrollStatus.PAID]: payments.fullPath,
      [PayrollStatus.PAYMENT_PROCESSING]: payments.fullPath,
    }
  }

  const navigationMap = createNavigationMap()

  const navigateToTab = (navigationStatus: PayrollStatus) => {
    if (policyId == null || periodId == null) {
      throw new Error('policyId and periodId must be defined')
    }
    const pathParams = { policyId, periodId }
    const navigatePath = navigationMap[navigationStatus]
    if (!navigatePath) {
      throw new Error(`Invalid status: ${navigationStatus}`)
    }
    return navigatePath(pathParams)
  }

  const onStartCycleClick = () => {
    track(trackingEvents.payroll.userStartPayrollCycle, {
      status,
      company_id: companyId,
    })
  }

  return (
    <Box
      flexDirection="row"
      borderRadius={{
        all: 'abs016',
      }}
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
    >
      <Box style={{ flex: '1' }} paddingX="s24" paddingY="s16" gap="s16">
        <Text.Header2>
          {i18n.t('payroll_bundle.payroll_dashboard.regular_cycle_summary.status_overview.title')}
        </Text.Header2>
        <Text.Normal>
          {i18n.t(
            'payroll_bundle.payroll_dashboard.regular_cycle_summary.status_overview.description'
          )}
        </Text.Normal>
        <Box flexDirection="row" alignItems="flexEnd" flexGrow>
          <Button.Router
            to={newCycleWorflowCtaPath()}
            onClick={onStartCycleClick}
            iconName={
              status === PayrollStatus.PREPARATION && canEditSupplements ? 'play' : 'arrow-right'
            }
          >
            {getDashboardButtonCta(status, canEditSupplements, canSeeCompanyPayrollResults)}
          </Button.Router>
        </Box>
      </Box>
      <Box width="s1" background="grey300" />
      <Box style={{ flex: '1' }} paddingX="s24" paddingY="s16" gap="s12">
        <Text.Header3>
          {i18n.t(
            'payroll_bundle.payroll_dashboard.regular_cycle_summary.status_overview.steps_title'
          )}
        </Text.Header3>
        <NavigationLink
          iconVisible={supplementsCompleted}
          label={i18n.t(
            'payroll_bundle.payroll_dashboard.regular_cycle_summary.status_overview.steps.supplements'
          )}
          onClick={() => navigate(navigateToTab(PayrollStatus.SUPPLEMENTS_DEFINITION))}
        />
        <NavigationLink
          iconVisible={resultsCompleted}
          label={i18n.t(
            'payroll_bundle.payroll_dashboard.regular_cycle_summary.status_overview.steps.results'
          )}
          disabled={!canSeeCompanyPayrollResults}
          onClick={() => navigate(navigateToTab(PayrollStatus.PAYROLL_CALCULATION))}
        />
        <NavigationLink
          iconVisible={paymentsCompleted}
          label={i18n.t(
            'payroll_bundle.payroll_dashboard.regular_cycle_summary.status_overview.steps.payments'
          )}
          disabled={paymentsDisabled}
          onClick={() => navigate(navigateToTab(PayrollStatus.PAYMENT_PROCESSING))}
        />
      </Box>
    </Box>
  )
}

export default ProcessPayrollStatusOverview
